import { useEffect, useState } from 'react';

import {
  AppIcon,
  type FilterSelectOption,
  HorizontalMenuTabs,
  LabeledFilterMultiSelect,
  LabeledFilterSelect,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ALL_FILTER_VALUE } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import {
  Box,
  Button,
  Drawer,
  IconButton,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

import { humanize } from './analytics.labels';
import { useAgentContext } from './AnalyticsFilterBar.agentContext';

export type FilterOption = FilterSelectOption;

/** Multi-valued dimensions hold the chosen values; an empty list is All. */
export type AnalyticsFilterValues = Readonly<{
  institution: readonly string[];
  programs: string;
  grade: readonly string[];
  enrollment: readonly string[];
  coaches: readonly string[];
  gender: readonly string[];
  ethnicity: readonly string[];
  cohorts: readonly string[];
}>;

type AnalyticsFilterKey = keyof AnalyticsFilterValues;

export type AnalyticsMultiFilterKey = Exclude<AnalyticsFilterKey, 'programs'>;

const { filters: filterStrings } = appStrings.modules.coachingAnalytics;

const ALL_OPTION: FilterOption = { value: ALL_FILTER_VALUE, label: filterStrings.all };

/** The un-narrowed cohort. Cache keys use these values, so passing {} refetches the same data. */
export const DEFAULT_FILTERS: AnalyticsFilterValues = {
  institution: [],
  programs: ALL_FILTER_VALUE,
  grade: [],
  enrollment: [],
  coaches: [],
  gender: [],
  ethnicity: [],
  cohorts: [],
};

type AnalyticsFilterBarProps = Readonly<{
  /** Collapses the fields into a drawer behind a Filters button. */
  isCompact: boolean;
  /** Without this, a failed options fetch is indistinguishable from an empty cohort. */
  isOptionsUnavailable: boolean;
  filters: AnalyticsFilterValues;
  onProgramChange: (value: string) => void;
  onMultiFilterChange: (key: AnalyticsMultiFilterKey, values: string[]) => void;
  onClear: () => void;
  /** Omitted for a coach, whose population is not an institution's. */
  institutionOptions?: readonly FilterOption[];
  /** Institution menu items that change the context rather than the narrowing. */
  institutionActions?: readonly FilterOption[];
  onInstitutionAction?: (value: string) => void;
  /** Replaces the All label on the institution select. */
  institutionAllLabel?: string;
  programOptions: readonly FilterOption[];
  gradeOptions: readonly FilterOption[];
  enrollmentOptions: readonly FilterOption[];
  /** Omitted for a coach, who could only ever select themselves. */
  coachOptions?: readonly FilterOption[];
  genderOptions: readonly FilterOption[];
  ethnicityOptions: readonly FilterOption[];
  cohortOptions: readonly FilterOption[];
}>;

type FilterField = Readonly<{
  key: AnalyticsFilterKey;
  label: string;
  /** Without the All entry. */
  options: readonly FilterOption[];
  wide?: boolean;
}>;

const isMultiKey = (key: AnalyticsFilterKey): key is AnalyticsMultiFilterKey => key !== 'programs';

export const AnalyticsFilterBar = ({
  isCompact,
  isOptionsUnavailable,
  filters,
  onProgramChange,
  onMultiFilterChange,
  onClear,
  institutionOptions,
  institutionActions,
  onInstitutionAction,
  institutionAllLabel,
  programOptions,
  gradeOptions,
  enrollmentOptions,
  coachOptions,
  genderOptions,
  ethnicityOptions,
  cohortOptions,
}: AnalyticsFilterBarProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Without this the drawer reopens by itself on the way back to a narrow column.
  useEffect(() => setDrawerOpen(false), [isCompact]);

  const maybeFields: readonly (FilterField | undefined)[] = [
    institutionOptions && {
      key: 'institution',
      label: filterStrings.institution,
      options: institutionOptions,
      wide: true,
    },
    { key: 'grade', label: filterStrings.grade, options: gradeOptions },
    { key: 'enrollment', label: filterStrings.enrollment, options: enrollmentOptions },
    coachOptions && { key: 'coaches', label: filterStrings.coaches, options: coachOptions },
    { key: 'cohorts', label: filterStrings.cohorts, options: cohortOptions, wide: true },
    { key: 'gender', label: filterStrings.gender, options: genderOptions },
    { key: 'ethnicity', label: filterStrings.ethnicity, options: ethnicityOptions },
  ];

  const fields = maybeFields.filter((field): field is FilterField => Boolean(field));
  const programField: FilterField = {
    key: 'programs',
    label: filterStrings.programs,
    options: programOptions,
  };
  const allFields = [programField, ...fields];
  const hasAnyFilter = allFields.some((field) => {
    const selected = filters[field.key];

    return typeof selected === 'string' ? selected !== ALL_FILTER_VALUE : selected.length > 0;
  });
  const clearButton = hasAnyFilter ? (
    <Button
      id="coaching-analytics-clear-filters-btn"
      hierarchy="Tertiary color"
      size="sm"
      sx={clearBtnSx}
      onClick={onClear}
    >
      {filterStrings.clear}
    </Button>
  ) : null;

  const optionLabel = (field: FilterField, value: string) =>
    field.options.find((option) => option.value === value)?.label ?? humanize(value);

  const selectionLabel = (field: FilterField) => {
    const selected = filters[field.key];

    if (typeof selected === 'string') {
      return selected === ALL_FILTER_VALUE ? filterStrings.all : optionLabel(field, selected);
    }

    return selected.length
      ? selected.map((value) => optionLabel(field, value)).join(', ')
      : filterStrings.all;
  };

  useAgentContext(Object.fromEntries(allFields.map((field) => [field.key, selectionLabel(field)])));

  const programChoices = [ALL_OPTION, ...programOptions];
  const programIndex = Math.max(
    0,
    programChoices.findIndex((option) => option.value === filters.programs),
  );
  const programTabs = (
    <Box sx={programsSx}>
      <HorizontalMenuTabs
        id="coaching-analytics-filter-programs"
        variant="filter"
        tabs={programChoices.map((option) =>
          option.value === ALL_FILTER_VALUE ? filterStrings.allStudents : option.label,
        )}
        value={programIndex}
        setValue={(index) => onProgramChange(programChoices[index]?.value ?? ALL_FILTER_VALUE)}
        containerSxOverride={programTabsSx}
      />
    </Box>
  );

  const renderSelect = (field: FilterField) => {
    const id = `coaching-analytics-filter-${field.key}`;
    const { key } = field;
    const isInstitution = key === 'institution';
    const allOption: FilterOption =
      isInstitution && institutionAllLabel
        ? { value: ALL_FILTER_VALUE, label: institutionAllLabel }
        : ALL_OPTION;
    const actions = isInstitution ? (institutionActions ?? []) : [];

    // A dimension with one choice narrows to nothing, so it stays a plain All/value select
    // unless a selection is already held.
    if (isMultiKey(key) && (field.options.length > 1 || filters[key].length > 0)) {
      // The other filters narrow these options, so a held value can drop out of them.
      const heldOptions = filters[key]
        .filter((value) => !field.options.some((option) => option.value === value))
        .map((value) => ({ value, label: value }));

      return (
        <LabeledFilterMultiSelect
          id={id}
          label={field.label}
          placeholder={allOption.label}
          allOption={allOption}
          options={[...field.options, ...heldOptions]}
          values={filters[key]}
          onChange={(values) => onMultiFilterChange(key, values)}
          actionOptions={actions}
          onAction={onInstitutionAction}
        />
      );
    }

    return (
      <LabeledFilterSelect
        id={id}
        label={field.label}
        value={isMultiKey(key) ? (filters[key][0] ?? ALL_FILTER_VALUE) : filters[key]}
        options={[allOption, ...field.options, ...actions]}
        onChange={(next) => {
          if (actions.some((action) => action.value === next)) {
            onInstitutionAction?.(next);
          } else if (isMultiKey(key)) {
            onMultiFilterChange(key, next === ALL_FILTER_VALUE ? [] : [next]);
          } else {
            onProgramChange(next);
          }
        }}
      />
    );
  };

  const unavailableNotice = isOptionsUnavailable ? (
    <Typography variant="text-sm-regular" sx={unavailableSx}>
      {filterStrings.unavailable}
    </Typography>
  ) : null;

  if (!isCompact) {
    return (
      <Box sx={barSx}>
        {unavailableNotice}
        <Box sx={fieldsRowSx}>
          {fields.map((field) => (
            <Box key={field.key} sx={field.wide ? wideFieldSx : fieldSx}>
              {renderSelect(field)}
            </Box>
          ))}
          {clearButton}
        </Box>
        {programTabs}
      </Box>
    );
  }

  return (
    <Box sx={barSx}>
      {unavailableNotice}
      <Box sx={compactRowSx}>
        <Button
          id="coaching-analytics-open-filters-btn"
          hierarchy="Secondary gray"
          size="md"
          sx={filtersButtonSx}
          startIcon={
            <AppIcon
              iconName="FilterLines"
              diameter="16px"
              color="Colors/Foreground/fg-quaternary"
            />
          }
          onClick={() => setDrawerOpen(true)}
        >
          {filterStrings.title}
        </Button>
        {clearButton}
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={drawerHeaderSx}>
          <Typography variant="text-lg-semibold">{filterStrings.title}</Typography>
          <IconButton
            id="coaching-analytics-close-filters-btn"
            onClick={() => setDrawerOpen(false)}
          >
            <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
          </IconButton>
        </Box>
        <Box sx={drawerFieldsSx}>
          {fields.map((field) => (
            <Box key={field.key} sx={drawerFieldSx}>
              {renderSelect(field)}
            </Box>
          ))}
        </Box>
      </Drawer>
      {programTabs}
    </Box>
  );
};

const barSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Beige/50'],
  '@container (min-width: 650px)': {
    paddingX: backrs.spacing['spacing-xl'],
  },
});

const programsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};

const programTabsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-senary']}`,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Beige/25'],
  '& .MuiTab-root': { zIndex: 1, whiteSpace: 'nowrap' },
  '& .MuiTabs-indicator': {
    zIndex: 0,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    boxShadow: backrs.shadows['shadow-xs'],
  },
});

const unavailableSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  paddingBottom: backrs.spacing['spacing-md'],
});

const fieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
  '@container (min-width: 1200px)': {
    flexWrap: 'nowrap',
  },
});

const clearBtnSx: SxProps<Theme> = {
  flexShrink: 0,
  alignSelf: 'flex-end',
};

const filtersButtonSx: SxProps<Theme> = {
  flex: 1,
};

const compactRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const drawerPaperSx: SxProps<Theme> = ({ backrs }) => ({
  width: `min(320px, calc(100vw - ${backrs.spacing['spacing-4xl']}))`,
  padding: backrs.spacing['spacing-2xl'],
});

const drawerHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: backrs.spacing['spacing-2xl'],
});

const drawerFieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const drawerFieldSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
};

const fieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  flex: '1 1 140px',
  '@container (min-width: 1200px)': {
    flex: '1 1 0',
  },
};

const wideFieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  flex: '1 1 220px',
  '@container (min-width: 1200px)': {
    flex: '1.6 1 0',
  },
};
